import React, { useEffect, useMemo, useState } from 'react'
import { ArrowLeft, Search, Link2, Sparkles, Plus } from 'lucide-react'
import {
  projectsApi,
  AiSuggestionRow,
  AutoMapSessionStatus,
  ProjectSystem,
  RaBucket,
  RaDocumentRow,
  RaSystemAssessmentRow,
} from '@/lib/projectsApi'
import { useToast } from '@/contexts/ToastContext'
import { Button } from '@/components/ui/Button'
import { AutoMapConfirmModal } from '@/components/projects/AutoMapConfirmModal'
import { AutoMapProgressModal } from '@/components/projects/AutoMapProgressModal'
import { AutoMapReviewPane } from '@/components/projects/AutoMapReviewPane'
import { CreateProjectSystemModal } from '@/components/projects/CreateProjectSystemModal'
import { useTranslation } from 'react-i18next'

interface Props {
  raDocumentId: string
  projectId:    string
  canEdit:      boolean
  // optional back action. kept for backwards-compat with any caller
  // (Phase B drawer used it. the Phase E2 tab refactor hides the button
  // when no callback is supplied)
  onBack?:      () => void
  onSaved?:     () => void   // notifies the parent so any open System
                              // Detail pages know to refetch
  // when the auto-map progress modal hits a terminal state, hand the final
  // session payload up. the tab container uses it to switch sub-tabs to the
  // AI Suggestions surface. legacy drawer callers can omit it and the panel
  // renders the review inline
  onAutoMapTerminal?: (status: AutoMapSessionStatus) => void
  // Phase E1.5 resume: if the parent detects a running auto-map session on
  // mount, it passes the session_id here so the progress modal re-attaches
  // without the user re-clicking Auto-map
  resumeSessionId?:    string | null
}

type FilterMode = 'all' | 'mapped' | 'unmapped' | 'pending'

const BUCKET_LABEL: Record<RaBucket, string> = {
  target:     'Target',
  exclusion:  'Exclusion',
  non_target: 'Non-target',
  ip_based:   'IP-based',
  pending:    'Pending',
}

/**
 * Post-commit re-mapping surface. Lets a user fix
 * ra_system_assessments.project_system_id for any row in an existing
 * ra_document without re-uploading the Word file.
 *
 * Layout mirrors the import preview's matching pane: one row per
 * assessment with a dropdown of the project's project_systems. Dirty
 * rows tracked locally. Save sends the whole diff as a batch PATCH so
 * all rows go in one transaction.
 */
export const WordRaMappingsPanel: React.FC<Props> = ({ raDocumentId, projectId, canEdit, onBack, onSaved, onAutoMapTerminal, resumeSessionId }) => {
  const { t } = useTranslation()
  const { error: showError, success } = useToast()

  const [document,       setDocument]       = useState<RaDocumentRow | null>(null)
  const [assessments,    setAssessments]    = useState<RaSystemAssessmentRow[]>([])
  const [projectSystems, setProjectSystems] = useState<ProjectSystem[]>([])
  const [originalMap,    setOriginalMap]    = useState<Record<string, string | null>>({})
  const [edited,         setEdited]         = useState<Record<string, string | null>>({})
  const [loading,        setLoading]        = useState(true)
  const [saving,         setSaving]         = useState(false)

  const [filter, setFilter] = useState<FilterMode>('all')
  const [search, setSearch] = useState('')

  // auto-map state. confirmOpen drives the cost-estimate modal.
  // autoMapSessionId is set when the user starts a run and drives the
  // progress modal (state==='running'). localSuggestions holds the
  // persisted rows used by the legacy inline review surface. The review
  // component no longer accepts the old live-session status shape.
  const [confirmOpen,      setConfirmOpen]      = useState(false)
  const [autoMapSessionId, setAutoMapSessionId] = useState<string | null>(resumeSessionId ?? null)
  const [localSuggestions, setLocalSuggestions] = useState<AiSuggestionRow[] | null>(null)
  // Phase 3: "+ Create from this row" affordance on unmapped rows.
  // createForAssessment holds the assessment row whose Create modal is
  // open. null when no modal is open
  const [createForAssessment, setCreateForAssessment] = useState<RaSystemAssessmentRow | null>(null)

  // Phase E1.5 resume: if the parent later detects a running session
  // (mount race against the doc data load), pick it up
  useEffect(() => {
    if (resumeSessionId && !autoMapSessionId) setAutoMapSessionId(resumeSessionId)
  }, [resumeSessionId, autoMapSessionId])

  // initial load: fetch the ra_document (with nested assessments) and the
  // project's project_systems in parallel
  useEffect(() => {
    let cancel = false
    setLoading(true)
    Promise.all([
      projectsApi.getRaDocument(raDocumentId),
      projectsApi.listProjectSystems(projectId),
    ])
      .then(([raDoc, psList]) => {
        if (cancel) return
        setDocument(raDoc.document)
        setAssessments(raDoc.assessments)
        setProjectSystems(psList)
        const m: Record<string, string | null> = {}
        for (const a of raDoc.assessments) m[a.id] = a.project_system_id ?? null
        setOriginalMap(m)
        setEdited(m)
      })
      .catch((err: any) => showError(err.message ?? t('wordRa.loadFailed')))
      .finally(() => { if (!cancel) setLoading(false) })
    return () => { cancel = true }
  }, [raDocumentId, projectId, showError, t])

  // dirty diff: which keys changed from their original mapping?
  const dirtyKeys = useMemo(() => {
    return Object.keys(edited).filter(k => (edited[k] ?? null) !== (originalMap[k] ?? null))
  }, [edited, originalMap])

  // mapped / unmapped count across ALL buckets. pending rows are mappable
  // now too (bucket is informational, not a permission gate). pending stays
  // a separate facet so users can still filter to just the Pending bucket
  // via the dropdown
  const counts = useMemo(() => {
    let mapped = 0, unmapped = 0, pending = 0
    for (const a of assessments) {
      if (a.bucket === 'pending') pending++
      if (edited[a.id]) mapped++
      else              unmapped++
    }
    return { mapped, unmapped, pending, total: assessments.length }
  }, [assessments, edited])

  // filter + search to keep the list manageable. mapped/unmapped facets
  // include pending rows so users can find unmapped Pending systems
  const visible = useMemo(() => {
    const q = search.toLowerCase().trim()
    return assessments.filter(a => {
      if (q && !(a.system_name.toLowerCase().includes(q) || (a.supplier ?? '').toLowerCase().includes(q))) {
        return false
      }
      if (filter === 'pending')  return a.bucket === 'pending'
      if (filter === 'mapped')   return !!edited[a.id]
      if (filter === 'unmapped') return !edited[a.id]
      return true
    })
  }, [assessments, edited, filter, search])

  const handleSave = async () => {
    if (dirtyKeys.length === 0) return
    setSaving(true)
    try {
      const updates = dirtyKeys.map(id => ({
        ra_system_assessment_id: id,
        project_system_id:       edited[id] ?? null,
      }))
      const result = await projectsApi.patchRaDocumentMappings(raDocumentId, updates)
      success(t('wordRa.changesPending', { count: result.updated_count }))
      setOriginalMap({ ...edited })
      onSaved?.()
    } catch (err: any) {
      showError(err.message ?? t('wordRa.deleteFailed'))
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => setEdited({ ...originalMap })

  // lookup maps for the review pane, built from the data the list already
  // loaded so the review surface never has to refetch
  const projectSystemsById = useMemo(() => {
    const m = new Map<string, ProjectSystem>()
    for (const ps of projectSystems) m.set(ps.id, ps)
    return m
  }, [projectSystems])

  const reloadAfterAutoMapAccept = async () => {
    setLocalSuggestions(null)
    setAutoMapSessionId(null)
    // re-pull the document so the table reflects the freshly-accepted
    // mappings. keep the loading shimmer brief
    setLoading(true)
    try {
      const raDoc = await projectsApi.getRaDocument(raDocumentId)
      setAssessments(raDoc.assessments)
      const m: Record<string, string | null> = {}
      for (const a of raDoc.assessments) m[a.id] = a.project_system_id ?? null
      setOriginalMap(m)
      setEdited(m)
    } finally {
      setLoading(false)
    }
    onSaved?.()
  }

  if (loading) return <div className="p-6 text-sm text-slate-400">{t('wordRa.loadingMappings')}</div>

  // legacy drawer behaviour: when no onAutoMapTerminal hook is wired up
  // (Phase B caller), render the review pane inline as a third local view.
  // the tab-mode caller (Phase E2) supplies the callback so the review
  // surface lives in its own sub-tab instead
  if (localSuggestions && !onAutoMapTerminal) {
    return (
      <AutoMapReviewPane
        raDocumentId={raDocumentId}
        projectId={projectId}
        canEdit={canEdit}
        suggestions={localSuggestions}
        projectSystemsById={projectSystemsById}
        onBack={() => { setLocalSuggestions(null); setAutoMapSessionId(null) }}
        onChanged={() => {
          void projectsApi.getAiSuggestions(raDocumentId, 'pending').then(r => {
            setLocalSuggestions(r.suggestions)
            if (r.suggestions.length === 0) void reloadAfterAutoMapAccept()
          }).catch(err => showError(err?.message ?? 'Failed to refresh suggestions'))
        }}
      />
    )
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-3">
        {onBack && (
          <button onClick={onBack} className="p-1 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
            <ArrowLeft className="h-4 w-4" />
          </button>
        )}
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold truncate">{t('wordRa.mappings')}</h2>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
            {document?.vessel_name ?? document?.project_name ?? t('wordRa.raDocument')}
            {document?.class_society ? ` · ${document.class_society}` : ''}
            {document?.revision ? ` · Rev. ${document.revision}` : ''}
          </div>
        </div>
      </div>

      <div className="px-5 py-3 border-b border-slate-200 dark:border-slate-700 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[160px]">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('wordRa.searchMapping')}
            className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 dark:border-slate-600 rounded bg-white dark:bg-slate-700 text-slate-900 dark:text-white"
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value as FilterMode)}
          className="text-xs border border-slate-300 dark:border-slate-600 dark:bg-slate-800 rounded px-2 py-1.5"
        >
          <option value="all">{t('wordRa.all')} ({counts.total})</option>
          <option value="mapped">{t('wordRa.mapped')} ({counts.mapped})</option>
          <option value="unmapped">{t('wordRa.unmapped')} ({counts.unmapped})</option>
          <option value="pending">{t('wordRa.pending')} ({counts.pending})</option>
        </select>
        {canEdit && (
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={counts.unmapped === 0 || autoMapSessionId !== null}
            title={counts.unmapped === 0
              ? 'Every row already has a mapping'
              : autoMapSessionId !== null
                ? 'An auto-map run is already in progress'
                : 'Run AI-assisted mapping for unmatched rows'}
            className="text-xs px-2 py-1.5 rounded bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white disabled:text-slate-500 flex items-center gap-1.5 whitespace-nowrap"
          >
            <Sparkles className="h-3.5 w-3.5" />
            {t('wordRa.autoMap')}
            {counts.unmapped > 0 && (
              <span className="text-[10px] opacity-90">({counts.unmapped})</span>
            )}
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-2 py-2">
        {visible.length === 0 ? (
          <div className="text-center py-8 text-xs text-slate-400">{t('wordRa.noRows')}</div>
        ) : (
          <table className="w-full text-[11px]">
            <thead className="sticky top-0 bg-white dark:bg-slate-800">
              <tr className="text-left text-[9px] text-slate-400 uppercase tracking-wide">
                <th className="px-2 py-1.5">{t('wordRa.wordSystem')}</th>
                <th className="px-2 py-1.5 w-14">{t('wordRa.bucket')}</th>
                <th className="px-2 py-1.5">{t('wordRa.projectSystem')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
              {visible.map(a => {
                const value     = edited[a.id] ?? ''
                const isDirty   = (edited[a.id] ?? null) !== (originalMap[a.id] ?? null)
                return (
                  <tr key={a.id} className={isDirty ? 'bg-amber-50/40 dark:bg-amber-900/10' : ''}>
                    <td className="px-2 py-1.5 align-top">
                      <div className="font-medium text-slate-800 dark:text-slate-100">{a.system_name}</div>
                      {a.supplier && <div className="text-[10px] text-slate-500">{a.supplier}</div>}
                    </td>
                    <td className="px-2 py-1.5 align-top text-slate-500">
                      <span className="text-[10px]">{t(`wordRa.${a.bucket === 'non_target' ? 'unmapped' : a.bucket}`, { defaultValue: BUCKET_LABEL[a.bucket] })}</span>
                    </td>
                    <td className="px-2 py-1.5 align-top">
                      {/* every bucket is mappable. the bucket badge in the
                          column to the left tells the user what kind of
                          Word RA row this is, the dropdown stays uniform.
                          Phase 3: source-suffix tags non-excel rows so
                          users can tell at a glance which inventory rows
                          came from CREATE or SPLIT. <option> nodes are
                          text-only so the badges have to be inline text */}
                      <div className="flex items-center gap-1.5">
                        <select
                            value={value}
                            disabled={!canEdit}
                            onChange={e => setEdited({ ...edited, [a.id]: e.target.value || null })}
                            className="text-[11px] border border-slate-300 dark:border-slate-600 dark:bg-slate-800 rounded px-2 py-1 flex-1 max-w-[280px] disabled:opacity-70"
                          >
                            <option value="">{t('wordRa.noMatch')}</option>
                            {projectSystems
                              .slice()
                              .sort((x, y) => (x.system_name ?? '').localeCompare(y.system_name ?? ''))
                              .map(ps => {
                                const sourceSuffix
                                  = ps.source === 'word_ra' ? ' [Word RA]'
                                  : ps.source === 'split'   ? ' [Split]'
                                  : ''
                                return (
                                  <option key={ps.id} value={ps.id}>
                                    {ps.system_name ?? t('wordRa.unnamed')}
                                    {ps.vendor_name_raw ? ` — ${ps.vendor_name_raw.slice(0, 30)}` : ''}
                                    {sourceSuffix}
                                  </option>
                                )
                              })}
                          </select>
                        {/* Phase 3: second entry point for CREATE. only
                            shows on unmapped rows (no inventory target
                            picked) so it doesn't compete with the dropdown
                            when a mapping already exists. same modal as the
                            AI Suggestions pane's [Create system…] button */}
                        {canEdit && !value && (
                          <button
                            type="button"
                            onClick={() => setCreateForAssessment(a)}
                            title="Create a new inventory row from this Word RA system"
                            className="text-[11px] px-1.5 py-1 rounded border border-emerald-300 text-emerald-700 dark:border-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 inline-flex items-center gap-1 whitespace-nowrap"
                          >
                            <Plus className="h-3 w-3" />
                            {t('wordRa.create')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-5 py-3 border-t border-slate-200 dark:border-slate-700 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <Link2 className="h-3.5 w-3.5" />
          {dirtyKeys.length === 0
            ? t('wordRa.noChanges')
            : t('wordRa.changesPending', { count: dirtyKeys.length })}
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleReset} disabled={saving || dirtyKeys.length === 0}>
              {t('wordRa.reset')}
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving || dirtyKeys.length === 0}>
              {saving ? t('wordRa.saving') : t('wordRa.saveCount', { count: dirtyKeys.length })}
            </Button>
          </div>
        )}
      </div>

      {canEdit && confirmOpen && (
        <AutoMapConfirmModal
          raDocumentId={raDocumentId}
          onClose={() => setConfirmOpen(false)}
          onStarted={(sessionId) => {
            setConfirmOpen(false)
            setAutoMapSessionId(sessionId)
          }}
        />
      )}

      {/* Phase 3: Create modal for unmapped rows. on success refetch
          project_systems so the new row appears in everyone's dropdown,
          then mark the source assessment mapped locally and dispatch the
          cross-tab event the AI Suggestions pane listens for */}
      {canEdit && createForAssessment && (
        <CreateProjectSystemModal
          projectId={projectId}
          assessment={{
            id:            createForAssessment.id,
            system_name:   createForAssessment.system_name,
            supplier:      createForAssessment.supplier,
            ship_function: createForAssessment.ship_function ?? null,
          }}
          onClose={() => setCreateForAssessment(null)}
          onCreated={async (newPs) => {
            const target = createForAssessment
            setCreateForAssessment(null)
            // refresh project_systems so the new row is selectable for
            // other Word RA rows too
            try {
              const psList = await projectsApi.listProjectSystems(projectId)
              setProjectSystems(psList)
            } catch { /* dropdown picks up new row on next load */ }
            // persist the new mapping for THIS row server-side so the
            // assessment is bound (the CREATE endpoint did this in the same
            // txn. mirror the local state)
            setEdited(prev => ({ ...prev, [target.id]: newPs.id }))
            setOriginalMap(prev => ({ ...prev, [target.id]: newPs.id }))
            window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
            onSaved?.()
          }}
          onMapToExisting={async (existingPsId) => {
            const target = createForAssessment
            try {
              await projectsApi.patchRaAssessmentMapping(target.id, existingPsId)
              success('Mapped to existing inventory row')
              setEdited(prev => ({ ...prev, [target.id]: existingPsId }))
              setOriginalMap(prev => ({ ...prev, [target.id]: existingPsId }))
              setCreateForAssessment(null)
              window.dispatchEvent(new CustomEvent('seacuredb:ra-mappings-updated'))
              onSaved?.()
            } catch (err: any) {
              showError(err?.message ?? 'Failed to map to existing row')
            }
          }}
        />
      )}

      {canEdit && autoMapSessionId && !localSuggestions && (
        <AutoMapProgressModal
          sessionId={autoMapSessionId}
          onTerminal={(finalStatus) => {
            // always surface the terminal state. even failed/cancelled runs
            // may have a partial suggestion list worth reviewing.
            // tab-mode caller (Phase E2): hand the status up via
            //   onAutoMapTerminal so the AI Suggestions sub-tab renders the
            //   review pane and the modal can close.
            // drawer-mode caller (legacy): no callback, fall back to the
            //   local third view
            if (onAutoMapTerminal) {
              setAutoMapSessionId(null)
              onAutoMapTerminal(finalStatus)
            } else {
              setAutoMapSessionId(null)
              void projectsApi.getAiSuggestions(raDocumentId, 'pending')
                .then(r => setLocalSuggestions(r.suggestions))
                .catch(err => showError(err?.message ?? 'Failed to load persisted suggestions'))
            }
          }}
        />
      )}
    </div>
  )
}
